import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Navigate, Link } from "react-router-dom";
import { Users, Shield, ShieldOff, X, Loader2, RefreshCw, Activity, UserCheck, Flag, Bus, Car, PawPrint, ChevronDown, ChevronRight, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import UserAvatar from "../components/UserAvatar";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "2-digit" });
  } catch {
    return iso;
  }
}

function RoleBadge({ role }) {
  const isAdmin = role === "admin";
  return (
    <span
      data-testid={`role-badge-${role}`}
      className={
        "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide " +
        (isAdmin
          ? "bg-brand-accent/15 text-brand-accent border border-brand-accent/30"
          : "bg-brand-sand text-brand-subtle border border-brand-border")
      }
    >
      {isAdmin ? <Shield size={11} /> : <UserCheck size={11} />}
      {isAdmin ? "Admin" : "User"}
    </span>
  );
}

/**
 * Pull the mode-of-transit off the Matchmaker profile safely.
 *
 * Users can arrive at this table in any of four states:
 *   - never onboarded → `humanProfile === undefined`
 *   - opened Profile but hit Save with empty dropdowns → `{ pace: null, transitReliance: null }`
 *   - filled only one field → `{ pace: "Active", transitReliance: null }`
 *   - fully filled                                   → `{ pace: "Active", transitReliance: "Car" }`
 * We treat everything except an exact "Transit" / "Car" string as "unknown"
 * so the icon column never crashes on a partially-filled row.
 */
function transitOf(u) {
  const val = u?.humanProfile?.transitReliance;
  if (val === "Transit") return "transit";
  if (val === "Car") return "car";
  return "unknown";
}

function TransitCell({ mode, testid }) {
  if (mode === "transit") {
    return (
      <span
        data-testid={testid}
        title="Transit reliant"
        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-primary/10 text-brand-primary border border-brand-primary/30"
      >
        <Bus size={14} />
      </span>
    );
  }
  if (mode === "car") {
    return (
      <span
        data-testid={testid}
        title="Car user"
        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-accent/10 text-brand-accent border border-brand-accent/30"
      >
        <Car size={14} />
      </span>
    );
  }
  return (
    <span
      data-testid={testid}
      title="Not set"
      className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-sand text-brand-subtle border border-brand-border text-[10px]"
    >
      —
    </span>
  );
}

/**
 * Compact one-line summary of the dog profile — used in the Pack column so
 * the admin can eyeball who has a Matchmaker profile filled in at all.
 * Returns null (rendered as an em-dash) when neither breed nor activity is set.
 */
function packSummary(u) {
  const d = u?.dogProfile;
  if (!d) return null;
  const parts = [];
  if (d.breed && String(d.breed).trim()) parts.push(String(d.breed).trim());
  if (d.activityLevel) parts.push(String(d.activityLevel));
  return parts.length ? parts.join(" · ") : null;
}

/**
 * Human-readable label for `transitReliance`. The raw enum value is fine for
 * a form dropdown but reads terse in a profile summary — "Public transport"
 * is what a walker actually calls it.
 */
function transitLabel(val) {
  if (val === "Transit") return "Public transport";
  if (val === "Car") return "Car";
  return null;
}

/**
 * Format a dog age as the walker would read it — "6 months", "1 year",
 * "4 years". Falls back to `null` when unset so the caller can hide the
 * chip entirely rather than showing "0 years".
 */
function formatDogAge(age) {
  if (typeof age !== "number" || isNaN(age) || age < 0) return null;
  if (age === 0) return null;
  if (age < 1) {
    const months = Math.round(age * 12);
    return months <= 1 ? "1 month" : `${months} months`;
  }
  const rounded = Math.round(age * 10) / 10;
  return rounded === 1 ? "1 year" : `${rounded} years`;
}

/**
 * Panel for the human half of the Matchmaker profile. Uses a two-line
 * "Pace: X · Transit: Y" summary with muted labels + bold values so an
 * admin can scan a page of walkers in one pass. Missing fields render as
 * a muted "Not set" so it's clear the field is empty, not broken.
 */
function HumanProfilePanel({ user }) {
  const h = user?.humanProfile;
  const hasAny = h && (h.pace || h.transitReliance);
  return (
    <div className="td-card p-4 bg-brand-surface" data-testid={`profile-human-${user.user_id}`}>
      <div className="td-section-label flex items-center gap-1.5">
        <UserCheck size={11} /> Human profile
      </div>
      {!hasAny ? (
        <p className="mt-2 text-sm text-brand-subtle italic" data-testid={`profile-human-${user.user_id}-empty`}>
          No human profile saved.
        </p>
      ) : (
        <dl className="mt-3 space-y-2 text-sm">
          <div className="flex items-baseline gap-2">
            <dt className="text-brand-subtle w-16 shrink-0 text-xs uppercase tracking-wider">Pace</dt>
            <dd
              className={h.pace ? "text-brand-text font-medium" : "text-brand-subtle italic"}
              data-testid={`profile-human-${user.user_id}-pace`}
            >
              {h.pace || "Not set"}
            </dd>
          </div>
          <div className="flex items-baseline gap-2">
            <dt className="text-brand-subtle w-16 shrink-0 text-xs uppercase tracking-wider">Transit</dt>
            <dd
              className={h.transitReliance ? "text-brand-text font-medium" : "text-brand-subtle italic"}
              data-testid={`profile-human-${user.user_id}-transit`}
            >
              {transitLabel(h.transitReliance) || "Not set"}
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
}

/**
 * Panel for the four-legged half of the Matchmaker profile.
 *
 * Headline reads naturally ("Ollie (4 years) · Border Collie · High energy")
 * so the admin can eyeball a whole page of packs. When a dog only has a name
 * we degrade gracefully — no dangling separators, no "undefined years".
 */
function DogProfilePanel({ user }) {
  const d = user?.dogProfile;
  const hasAny = d && (d.name || d.breed || (typeof d.age === "number" && d.age > 0) || d.activityLevel);
  if (!hasAny) {
    return (
      <div className="td-card p-4 bg-brand-surface" data-testid={`profile-dog-${user.user_id}`}>
        <div className="td-section-label flex items-center gap-1.5">
          <PawPrint size={11} /> Dog profile
        </div>
        <p className="mt-2 text-sm text-brand-subtle italic" data-testid={`profile-dog-${user.user_id}-empty`}>
          No dog profile saved.
        </p>
      </div>
    );
  }

  const displayName = (d.name && String(d.name).trim()) || "Dog";
  const ageLabel = formatDogAge(d.age);
  const activityLabel = d.activityLevel ? `${d.activityLevel} energy` : null;

  // Build the "chip trail" from whatever fields exist. Skipping falsy entries
  // means "Ollie" alone renders as just "Ollie" — no trailing dots.
  const chips = [
    d.breed && String(d.breed).trim(),
    activityLabel,
  ].filter(Boolean);

  return (
    <div className="td-card p-4 bg-brand-surface" data-testid={`profile-dog-${user.user_id}`}>
      <div className="td-section-label flex items-center gap-1.5">
        <PawPrint size={11} /> Dog profile
      </div>
      <div className="mt-2">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span
            className="font-heading text-lg text-brand-text leading-tight"
            data-testid={`profile-dog-${user.user_id}-name`}
          >
            {displayName}
          </span>
          {ageLabel && (
            <span
              className="text-sm text-brand-subtle"
              data-testid={`profile-dog-${user.user_id}-age`}
            >
              ({ageLabel})
            </span>
          )}
        </div>
        {chips.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {chips.map((c, i) => (
              <span
                key={i}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-accent/10 text-brand-accent border border-brand-accent/25"
                data-testid={`profile-dog-${user.user_id}-chip-${i}`}
              >
                {c}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const { user, loading } = useAuth();
  const qc = useQueryClient();
  const [pending, setPending] = useState(null); // { user_id, email, fromRole, toRole }
  const [expandedId, setExpandedId] = useState(null); // user_id of the row whose debug panel is open

  const usersQ = useQuery({
    queryKey: ["admin-users"],
    enabled: !!user && !!user.is_admin,
    queryFn: async () => (await api.get("/admin/users")).data,
  });

  const roleMut = useMutation({
    mutationFn: async ({ userId, role }) =>
      (await api.patch(`/admin/users/${userId}/role`, { role })).data,
    onSuccess: (_data, vars) => {
      toast.success(vars.role === "admin" ? "Promoted to admin" : "Demoted to user");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      setPending(null);
    },
    onError: (err) => {
      const msg = err?.response?.data?.detail || "Couldn't update role";
      toast.error(msg);
      setPending(null);
    },
  });

  const removeAvatarMut = useMutation({
    mutationFn: async (userId) => (await api.delete(`/admin/users/${userId}/avatar`)).data,
    onSuccess: () => {
      toast.success("Profile photo removed");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || "Couldn't remove photo");
    },
  });

  const data = usersQ.data;
  const users = data?.users || [];

  // Derived metrics — computed only when the users list changes so scrolling
  // and row-expansions don't re-run the reduce.
  const { transitPct, topBreed, topBreedCount } = useMemo(() => {
    if (!users.length) return { transitPct: 0, topBreed: null, topBreedCount: 0 };
    // Denominator = walkers who actually filled in `transitReliance` (any
    // value). A "% of everyone" number would penalise the metric for every
    // new signup who hasn't opened Profile yet — not useful.
    let filled = 0;
    let transit = 0;
    const breedTally = new Map();
    for (const u of users) {
      const t = u?.humanProfile?.transitReliance;
      if (t === "Transit" || t === "Car") {
        filled += 1;
        if (t === "Transit") transit += 1;
      }
      const raw = u?.dogProfile?.breed;
      const trimmed = typeof raw === "string" ? raw.trim() : "";
      if (trimmed) {
        // Normalise to Title Case so "border collie" and "Border Collie" bucket
        // together — but keep the display in the case the admin last typed.
        const key = trimmed.toLowerCase();
        breedTally.set(key, (breedTally.get(key) || 0) + 1);
      }
    }
    const pct = filled === 0 ? 0 : Math.round((transit / filled) * 100);
    let topKey = null;
    let topN = 0;
    for (const [k, n] of breedTally.entries()) {
      if (n > topN) { topKey = k; topN = n; }
    }
    // Preserve the user-typed casing for display by finding any matching row.
    let display = null;
    if (topKey) {
      const src = users.find((u) => (u?.dogProfile?.breed || "").trim().toLowerCase() === topKey);
      display = src?.dogProfile?.breed?.trim() || topKey;
    }
    return { transitPct: pct, topBreed: display, topBreedCount: topN };
  }, [users]);

  if (loading) return <div className="p-8 text-brand-subtle">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;

  const currentUserId = data?.current_user_id || user.user_id;

  const handleClick = (u) => {
    const isAdminRow = u.role === "admin";
    if (isAdminRow) {
      setPending({ user_id: u.user_id, email: u.email, fromRole: "admin", toRole: "user" });
    } else {
      roleMut.mutate({ userId: u.user_id, role: "admin" });
    }
  };

  const confirmDemote = () => {
    if (!pending) return;
    roleMut.mutate({ userId: pending.user_id, role: pending.toRole });
  };

  const adminCount = users.filter((u) => u.role === "admin").length;

  const toggleRow = (uid) => setExpandedId((cur) => (cur === uid ? null : uid));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6" data-testid="admin-users-page">
      <header className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="td-section-label">Admin</div>
          <h1 className="font-heading text-4xl font-semibold text-brand-text mt-1 flex items-center gap-3">
            <Users size={28} className="text-brand-primary" />
            User management
          </h1>
          <p className="text-brand-subtle text-sm mt-1">
            Promote trusted walkers to admin or revoke access. Roles update instantly.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/admin/telemetry" data-testid="admin-users-to-telemetry" className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <Activity size={14} /> Telemetry
          </Link>
          <Link to="/admin/reports" data-testid="admin-users-to-reports" className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <Flag size={14} /> Reports
          </Link>
          <button data-testid="admin-users-refresh" onClick={() => usersQ.refetch()} className="td-btn-secondary !py-2 !px-4 !text-sm whitespace-nowrap">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </header>

      {/* Summary cards — 4-across on desktop, 2-across on mobile. The two
          new Matchmaker cards sit between Total users and Signed-in-as. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="users-summary">
        <div className="td-card p-4">
          <div className="td-section-label">Total users</div>
          <div className="font-heading text-3xl text-brand-text mt-1" data-testid="metric-total-users">
            {users.length.toLocaleString("en-GB")}
          </div>
          <div className="text-[11px] text-brand-subtle mt-0.5">{adminCount} admin{adminCount === 1 ? "" : "s"}</div>
        </div>
        <div className="td-card p-4">
          <div className="td-section-label flex items-center gap-1"><Bus size={11} /> Transit reliant</div>
          <div className="font-heading text-3xl text-brand-primary mt-1" data-testid="metric-transit-pct">
            {transitPct}%
          </div>
          <div className="text-[11px] text-brand-subtle mt-0.5">
            of walkers with a saved reliance
          </div>
        </div>
        <div className="td-card p-4">
          <div className="td-section-label flex items-center gap-1"><PawPrint size={11} /> Top dog breed</div>
          {topBreed ? (
            <>
              <div className="font-heading text-lg text-brand-accent mt-1 leading-tight truncate" data-testid="metric-top-breed">
                {topBreed}
              </div>
              <div className="text-[11px] text-brand-subtle mt-0.5">
                {topBreedCount} pack{topBreedCount === 1 ? "" : "s"}
              </div>
            </>
          ) : (
            <div className="font-heading text-lg text-brand-subtle mt-1" data-testid="metric-top-breed-empty">
              —
            </div>
          )}
        </div>
        <div className="td-card p-4">
          <div className="td-section-label">Signed in as</div>
          <div className="mt-1 flex items-center gap-2 text-brand-text">
            <span className="font-medium truncate text-xs">{user.email}</span>
          </div>
          <div className="mt-1">
            <RoleBadge role={user.role || "user"} />
          </div>
        </div>
      </div>

      {usersQ.isLoading && (
        <div className="td-card p-6 flex items-center gap-2 text-brand-subtle text-sm">
          <Loader2 size={16} className="animate-spin" /> Loading users…
        </div>
      )}

      {usersQ.isError && (
        <div className="td-card p-6 text-sm text-brand-error">
          {usersQ.error?.response?.data?.detail || "Couldn't load users."}
        </div>
      )}

      {data && (
        <div className="td-card p-0 overflow-hidden" data-testid="admin-users-table">
          <div className="px-5 sm:px-6 pt-5 pb-3 flex items-center justify-between">
            <h2 className="font-heading text-lg font-medium">All users</h2>
            <span className="text-xs text-brand-subtle">Tap any row for profile detail</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-brand-sand text-brand-subtle uppercase text-[11px] tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 w-8"></th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2 hidden sm:table-cell">Registered</th>
                  <th className="text-left px-3 py-2">Role</th>
                  <th className="text-center px-3 py-2 hidden md:table-cell">Transit</th>
                  <th className="text-left px-3 py-2 hidden md:table-cell">Pack</th>
                  <th className="text-right px-3 py-2 hidden lg:table-cell">Saved</th>
                  <th className="text-right px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = u.user_id === currentUserId;
                  const isAdminRow = u.role === "admin";
                  const isOpen = expandedId === u.user_id;
                  const rowCls = isSelf
                    ? "bg-brand-primary/5 border-l-4 border-l-brand-primary"
                    : "";
                  const pack = packSummary(u);
                  const mode = transitOf(u);
                  return (
                    <React.Fragment key={u.user_id}>
                      <tr
                        className={`border-t border-brand-border/40 cursor-pointer hover:bg-brand-sand/30 transition-colors ${rowCls}`}
                        data-testid={`user-row-${u.user_id}`}
                        onClick={() => toggleRow(u.user_id)}
                      >
                        <td className="px-3 py-3 align-top text-brand-subtle">
                          <button
                            data-testid={`user-row-toggle-${u.user_id}`}
                            aria-expanded={isOpen}
                            aria-label={isOpen ? "Collapse profile" : "Expand profile"}
                            onClick={(e) => { e.stopPropagation(); toggleRow(u.user_id); }}
                            className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-brand-sand"
                          >
                            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                        </td>
                        <td className="px-3 py-3 text-brand-text">
                          <div className="flex items-center gap-2">
                            <UserAvatar user={u} size={32} testId={`admin-user-avatar-${u.user_id}`} />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium break-all">{u.email}</span>
                                {isSelf && (
                                  <span
                                    data-testid="self-badge"
                                    className="text-[10px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-md bg-brand-primary text-brand-sand"
                                  >
                                    You
                                  </span>
                                )}
                              </div>
                              {u.name && <div className="text-xs text-brand-subtle mt-0.5">{u.name}</div>}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-brand-subtle hidden sm:table-cell whitespace-nowrap">
                          {fmtDate(u.created_at)}
                        </td>
                        <td className="px-3 py-3">
                          <RoleBadge role={u.role || "user"} />
                        </td>
                        <td className="px-3 py-3 text-center hidden md:table-cell">
                          <TransitCell mode={mode} testid={`transit-cell-${u.user_id}`} />
                        </td>
                        <td className="px-3 py-3 hidden md:table-cell text-brand-text">
                          {pack ? (
                            <span
                              data-testid={`pack-cell-${u.user_id}`}
                              className="inline-flex items-center gap-1.5 text-xs"
                            >
                              <PawPrint size={11} className="text-brand-accent shrink-0" />
                              <span className="truncate max-w-[180px]">{pack}</span>
                            </span>
                          ) : (
                            <span
                              data-testid={`pack-cell-${u.user_id}-empty`}
                              className="text-xs text-brand-subtle"
                            >
                              —
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right hidden lg:table-cell">
                          <span
                            data-testid={`saved-count-${u.user_id}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-brand-sand text-brand-text border border-brand-border tabular-nums"
                          >
                            {(u.saved_count || 0).toLocaleString("en-GB")}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <button
                            data-testid={`role-toggle-${u.user_id}`}
                            onClick={(e) => { e.stopPropagation(); handleClick(u); }}
                            disabled={roleMut.isPending && roleMut.variables?.userId === u.user_id}
                            className={
                              "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors " +
                              (isAdminRow
                                ? "bg-brand-surface text-brand-error border border-brand-error/40 hover:bg-brand-error/10"
                                : "bg-brand-primary text-brand-sand border border-brand-primary hover:bg-brand-primary/90") +
                              " disabled:opacity-60"
                            }
                          >
                            {roleMut.isPending && roleMut.variables?.userId === u.user_id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : isAdminRow ? (
                              <ShieldOff size={12} />
                            ) : (
                              <Shield size={12} />
                            )}
                            {isAdminRow ? "Demote" : "Promote"}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr
                          className="border-t border-brand-border/40 bg-brand-sand/40"
                          data-testid={`user-row-debug-${u.user_id}`}
                        >
                          <td className="px-3 py-3"></td>
                          <td colSpan={7} className="px-3 py-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <HumanProfilePanel user={u} />
                              <DogProfilePanel user={u} />
                            </div>
                            {u.avatar_url && (
                              <div className="mt-3 flex justify-end">
                                <button
                                  data-testid={`admin-remove-avatar-${u.user_id}`}
                                  onClick={() => removeAvatarMut.mutate(u.user_id)}
                                  disabled={removeAvatarMut.isPending && removeAvatarMut.variables === u.user_id}
                                  className="inline-flex items-center gap-1.5 text-xs text-brand-subtle hover:text-brand-error disabled:opacity-60"
                                >
                                  {removeAvatarMut.isPending && removeAvatarMut.variables === u.user_id
                                    ? <Loader2 size={12} className="animate-spin" />
                                    : <ImageOff size={12} />}
                                  Remove photo
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-5 py-6 text-center text-brand-subtle">
                      No users registered yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-xs text-brand-subtle mt-6">
        <Link to="/discover" className="underline" data-testid="admin-users-back">
          ← Back to app
        </Link>
      </p>

      {/* Demote confirmation modal */}
      {pending && pending.toRole === "user" && (
        <div
          className="fixed inset-0 z-[1100] bg-brand-text/60 flex items-center justify-center p-4"
          onClick={() => setPending(null)}
          data-testid="demote-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-brand-surface rounded-3xl w-full max-w-sm p-6 animate-fade-up"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl text-brand-text flex items-center gap-2">
                <ShieldOff size={18} className="text-brand-error" />
                Demote to user?
              </h3>
              <button
                onClick={() => setPending(null)}
                className="text-brand-subtle"
                aria-label="Close"
                data-testid="demote-close"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-brand-subtle mt-2">
              <span className="text-brand-text font-medium break-all">{pending.email}</span> will lose
              admin access immediately, including this dashboard.
            </p>
            {pending.user_id === currentUserId && (
              <p className="text-sm text-brand-error mt-2 font-medium">
                Heads up — that&apos;s <em>your</em> account. You won&apos;t be able to return to admin pages.
              </p>
            )}
            <div className="flex gap-2 mt-5">
              <button
                data-testid="demote-cancel"
                onClick={() => setPending(null)}
                className="td-btn-secondary flex-1 !py-2.5"
              >
                Cancel
              </button>
              <button
                data-testid="demote-confirm"
                onClick={confirmDemote}
                disabled={roleMut.isPending}
                className="td-btn-primary flex-1 !py-2.5 !bg-brand-error"
              >
                {roleMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <ShieldOff size={14} />}
                Yes, demote
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
